/* Throne theme JS.
   Section-aware: every feature hangs off a data-attribute hook, inits are
   idempotent (data-throne-init guard), and `shopify:section:load` re-inits
   whatever the theme editor injects. Modal + Escape handling is delegated
   at the document level, so it never needs re-wiring. */
(function () {
  'use strict';

  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Signed up already (popup or quiz): styles.css hides Alia's $50 signup for them
  try { if (localStorage.getItem('throne_subscribed')) document.documentElement.classList.add('is-subscribed'); } catch (e) {}

  // Opening Alia's full-screen popup locks the page (body overflow:hidden) even while styles.css
  // hides it (on the quiz, for signed-up visitors), which left those pages unable to scroll.
  // Undo that lock only while a hidden Alia popup is open; the theme's own locks are untouched.
  (function () {
    function hiddenAliaOpen() {
      return [].some.call(document.querySelectorAll('[id^="alia-root"]'), function (root) {
        return /inset: 0/.test(root.getAttribute('style') || '') && getComputedStyle(root).display === 'none';
      });
    }
    function release() { if (document.body.style.overflow === 'hidden' && hiddenAliaOpen()) document.body.style.overflow = ''; }
    if (!window.MutationObserver || !document.body) return;
    var watched = new MutationObserver(release);
    function watch() {
      document.querySelectorAll('[id^="alia-root"]').forEach(function (root) {
        if (root.hasAttribute('data-throne-alia')) return;
        root.setAttribute('data-throne-alia', '');
        watched.observe(root, { attributes: true, attributeFilter: ['style'] });
      });
      release();
    }
    watched.observe(document.body, { attributes: true, attributeFilter: ['style'] });
    new MutationObserver(watch).observe(document.body, { childList: true });
    watch();
  })();

  function guard(el) {
    if (el.hasAttribute('data-throne-init')) return false;
    el.setAttribute('data-throne-init', '');
    return true;
  }

  /* --- Card detail modal: clone a <template id="modal-*"> into the shell.
         Whole cards act as triggers (click + Enter/Space); templates with
         [data-video] swap the dialog for a 16:9 iframe that unloads on close. */
  var lastFocus = null;

  function modalEls() {
    var modal = document.getElementById('cardModal');
    return modal ? { modal: modal, content: document.getElementById('modalContent') } : null;
  }

  function showModal(content, video) {
    var els = modalEls();
    if (!els || !content) return;
    lastFocus = document.activeElement;
    els.content.innerHTML = '';
    els.content.classList.toggle('modal-dialog--video', !!video);
    els.content.appendChild(content);
    els.content.dispatchEvent(new CustomEvent('throne:modal:open', { bubbles: true }));
    els.content.scrollTop = 0;
    els.modal.classList.add('open');
    els.modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    var closeBtn = els.content.querySelector('[data-close]');
    if (closeBtn) closeBtn.focus();
  }

  function openModal(id) {
    var tpl = document.getElementById('modal-' + id);
    if (!tpl) return;
    showModal(tpl.content.cloneNode(true), tpl.hasAttribute('data-video'));
  }

  function closeModal() {
    var els = modalEls();
    if (!els) return;
    els.modal.classList.remove('open');
    els.modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    els.content.innerHTML = ''; // drops the iframe too, so video audio stops
    if (lastFocus && lastFocus.focus) {
      lastFocus.focus();
    }
  }

  document.addEventListener('click', function (e) {
    var closer = e.target.closest('[data-close]');
    if (closer) { e.preventDefault(); closeModal(); return; }
    var trigger = e.target.closest('[data-modal]');
    if (trigger) { e.preventDefault(); openModal(trigger.getAttribute('data-modal')); }
  });
  document.addEventListener('keydown', function (e) {
    var els = modalEls();
    if (e.key === 'Escape' && els && els.modal.classList.contains('open')) { closeModal(); return; }
    if (e.key === 'Tab' && els && els.modal.classList.contains('open')) {
      var focusable = Array.prototype.filter.call(
        els.content.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'),
        function (el) { return el.offsetParent !== null; }
      );
      if (!focusable.length) return;
      var first = focusable[0], last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      return;
    }
    if ((e.key === 'Enter' || e.key === ' ') && e.target.tagName !== 'SUMMARY' && e.target.hasAttribute && e.target.hasAttribute('data-modal')) {
      e.preventDefault();
      openModal(e.target.getAttribute('data-modal'));
    }
  });

  /* --- Header: hide on scroll down, return on scroll up + mobile menu.
         The menu is a dialog: focus moves to the close button on open and
         back to the toggle on close, Tab is trapped inside the sheet,
         Escape / backdrop / link clicks close it. */
  function initHeader(header) {
    if (!guard(header)) return;
    var lastY = window.scrollY, tick = false;
    // Announcement bar (homepage): the header sits below it and slides up
    // as the bar scrolls off.
    var announce = document.querySelector('[data-announce-bar]');
    function placeBelowAnnounce(y) {
      if (!announce) return;
      var h = announce.offsetHeight;
      document.documentElement.style.setProperty('--announce-offset', Math.max(0, h - y) + 'px');
    }
    placeBelowAnnounce(lastY);
    window.addEventListener('scroll', function () {
      if (tick) return; tick = true;
      requestAnimationFrame(function () {
        var y = window.scrollY;
        placeBelowAnnounce(y);
        if (y < 80) header.classList.remove('is-hidden');
        else if (y > lastY + 6) header.classList.add('is-hidden');
        else if (y < lastY - 6) header.classList.remove('is-hidden');
        lastY = y; tick = false;
      });
    }, { passive: true });

    // Disclosure nav: the toggle keeps focus and the pill morphs in
    // place (.menu-open); the overlay (header's sibling — fixed inside
    // the will-change:transform header would be clipped) only holds the
    // backdrop + panel.
    Array.prototype.forEach.call(header.querySelectorAll('[data-health]'), function (item) { initHealthMenu(header, item); });

    var toggle = header.querySelector('[data-nav-toggle]'),
        overlay = header.parentElement.querySelector('[data-mobile-nav]');
    if (!toggle || !overlay) return;

    // Second views inside the panel (Oura-style drill-in): "Your health" and "Partners".
    // Each [data-health-open="<name>"] button opens the [data-health-view="<name>"] view.
    var mainView = overlay.querySelector('[data-main-view]'),
        healthView = null,
        healthOpen = null;
    function showHealth(name) {
      var view = overlay.querySelector('[data-health-view="' + name + '"]');
      if (!mainView || !view) return;
      healthView = view;
      healthOpen = overlay.querySelector('[data-health-open="' + name + '"]');
      mainView.hidden = true; view.hidden = false;
      if (healthOpen) healthOpen.setAttribute('aria-expanded', 'true');
      view.querySelector('[data-health-back]').focus();
    }
    function showMain(restoreFocus) {
      if (!mainView || !healthView || healthView.hidden) return;
      healthView.hidden = true; mainView.hidden = false;
      if (healthOpen) healthOpen.setAttribute('aria-expanded', 'false');
      if (restoreFocus) {
        mainView.classList.add('is-returning');
        setTimeout(function () { mainView.classList.remove('is-returning'); }, 300);
        if (healthOpen) healthOpen.focus();
      }
    }

    function openMenu() {
      overlay.hidden = false;
      header.classList.add('menu-open');
      toggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    }
    function closeMenu() {
      overlay.hidden = true;
      header.classList.remove('menu-open');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      showMain(false);
      toggle.focus();
    }

    toggle.addEventListener('click', function () {
      overlay.hidden ? openMenu() : closeMenu();
    });
    overlay.addEventListener('click', function (e) {
      var sub = e.target.closest('[data-health-open]');
      if (sub) { showHealth(sub.getAttribute('data-health-open')); return; }
      if (e.target.closest('[data-health-back]')) { showMain(true); return; }
      if (e.target.closest('a')) { closeMenu(); return; } // let the link navigate, then close
      if (!e.target.closest('.mobile-nav__panel')) closeMenu(); // backdrop / empty gutter
    });
    // document-level: focus sits on the toggle (in the header), not in the overlay
    document.addEventListener('keydown', function (e) {
      if (overlay.hidden) return;
      if (e.key === 'Escape') { closeMenu(); return; }
      if (e.key !== 'Tab') return;
      var cycle = [toggle].concat(Array.prototype.filter.call(
        overlay.querySelectorAll('a[href], button:not([disabled])'),
        function (el) { return el.offsetParent !== null; } // skip the hidden view
      ));
      var first = cycle[0], last = cycle[cycle.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    // leaving the mobile breakpoint closes the menu so scroll isn't left locked
    var desktopMQ = window.matchMedia('(min-width: 900px)');
    var onBreakpointChange = function () {
      if (desktopMQ.matches && !overlay.hidden) closeMenu();
    };
    if (desktopMQ.addEventListener) desktopMQ.addEventListener('change', onBreakpointChange);
    else desktopMQ.addListener(onBreakpointChange); // older Safari
  }

  /* --- Header dropdowns (desktop): "Your health" and "Partners". Click or
         hover opens a panel under the pill; the pill morphs to eggshell
         (.health-open). Opening one closes the other. Escape / backdrop /
         outside click close it and return focus. */
  var healthMenus; // filled on first use: the header can initialize before this line runs
  function initHealthMenu(header, item) {
    if (!item) return;
    healthMenus = healthMenus || [];
    var btn = item.querySelector('[data-health-toggle]'),
        panel = item.querySelector('[data-health-panel]'),
        backdrop = header.parentElement.querySelector('[data-health-backdrop]'),
        hoverTimer = null;
    var canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

    function open() {
      clearTimeout(hoverTimer);
      if (!panel.hidden) return;
      healthMenus.forEach(function (m) { if (m !== menu) m.close(false, true); });
      if (panel.hasAttribute('data-health-anchor')) {
        // a small panel lines up under its own toggle, inside the pill
        var pill = header.querySelector('.nav-pill');
        if (pill) panel.style.setProperty('--menu-right', Math.max(16, pill.getBoundingClientRect().right - btn.getBoundingClientRect().right - 24) + 'px');
      }
      panel.hidden = false;
      if (backdrop) backdrop.hidden = false;
      header.classList.add('health-open');
      header.classList.remove('is-hidden');
      btn.setAttribute('aria-expanded', 'true');
    }
    function close(returnFocus, switching) {
      clearTimeout(hoverTimer);
      if (panel.hidden) return;
      panel.hidden = true;
      if (!switching) {
        if (backdrop) backdrop.hidden = true;
        header.classList.remove('health-open');
      }
      btn.setAttribute('aria-expanded', 'false');
      if (returnFocus) btn.focus();
    }

    var menu = { close: close };
    healthMenus.push(menu);
    btn.addEventListener('click', function () { panel.hidden ? open() : close(false); });
    item.addEventListener('pointerenter', function (e) {
      if (e.pointerType === 'mouse' && canHover.matches) { clearTimeout(hoverTimer); hoverTimer = setTimeout(open, 120); }
    });
    item.addEventListener('pointerleave', function (e) {
      if (e.pointerType === 'mouse' && canHover.matches) { clearTimeout(hoverTimer); hoverTimer = setTimeout(function () { close(false); }, 250); }
    });
    if (backdrop) backdrop.addEventListener('click', function () { close(false); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !panel.hidden) close(true);
    });
    document.addEventListener('focusin', function (e) {
      if (!panel.hidden && !item.contains(e.target)) close(false);
    });
    // scrolling away hides the pill; keep the panel from floating alone
    window.addEventListener('scroll', function () {
      if (!panel.hidden && window.scrollY > 80 && header.classList.contains('is-hidden')) close(false);
    }, { passive: true });
  }

  /* --- Steps: the markup is the static stacked design (per-step cards,
         used as-is on tablet/mobile, no-JS, and reduced motion). On
         desktop it's progressively enhanced into the pinned scroll
         sequence: images crossfade in a sticky left column while texts
         swap on the right — one viewport of runway per step. Crossing
         the 900px breakpoint restores the static markup and rebuilds. */
  var STEPS_MQ = window.matchMedia('(min-width: 900px)');

  function initSteps(container) {
    if (!guard(container)) return;
    // The editor owns block rendering and reordering. Keep the authored
    // articles untouched there and avoid installing viewport listeners.
    if (window.Shopify && window.Shopify.designMode) return;
    var staticHTML = container.innerHTML, teardown = null;

    function build() {
      if (REDUCE || !STEPS_MQ.matches || !window.gsap) return; // no gsap: static stacked markup stays
      var steps = Array.prototype.slice.call(container.querySelectorAll('.step'));
      if (steps.length < 2) return;

      var pin = document.createElement('div'); pin.className = 'steps-pin';
      pin.style.height = ((steps.length + 1) * 100) + 'vh';
      var stage = document.createElement('div'); stage.className = 'steps-stage';
      var frame = document.createElement('div'); frame.className = 'steps-frame';
      var media = document.createElement('div'); media.className = 'steps-frame__media';
      var textsCol = document.createElement('div'); textsCol.className = 'steps-frame__texts';
      steps.forEach(function (step) {
        var img = step.querySelector('.step__media img');
        if (img) media.appendChild(img);
        var text = document.createElement('div'); text.className = 'steps-frame__text';
        var content = step.querySelector('.step__content');
        if (content) text.appendChild(content);
        textsCol.appendChild(text);
      });
      frame.appendChild(media); frame.appendChild(textsCol);
      stage.appendChild(frame); pin.appendChild(stage);
      container.innerHTML = ''; container.appendChild(pin);
      container.classList.add('steps--pinned');

      var imgs = media.children, texts = textsCol.children, tick = false;

      /* Deck transitions: ohm.health's "Designed to slow you down" model
         (their inline GSAP source read 2026-07-23) — crossing a step
         boundary fires a TIMED, speed-independent crossfade, unlike the
         old build that scrubbed opacity 1:1 with scroll. Timings tuned
         with Lauren same day to sit BETWEEN ohm's floaty originals
         (power1.out, 1.1/1.2/1.4s, offsets .15/.35) and our old snap:
         power2.out, out .8s (text sinks y14), image in at +.1s growing
         1.02→1.06 over 1s, text in at +.22s rising y20→0 over .9s.
         Runway is 100vh per boundary (.steps-pin 400vh) so a single
         flick can't skip steps. Needs gsap (vendored for scroll-fx;
         build() bails to the static stack without it). */
      var current = -1;
      for (var ti = 0; ti < texts.length; ti++) texts[ti].style.pointerEvents = 'none';
      var show = function (next) {
        if (next === current) return;
        var pI = current >= 0 ? imgs[current] : null, pT = current >= 0 ? texts[current] : null;
        var nI = imgs[next], nT = texts[next];
        gsap.killTweensOf(pI ? [pI, pT, nI, nT] : [nI, nT]);
        var tl = gsap.timeline({ defaults: { ease: 'power2.out', duration: 0.8 }, overwrite: 'auto' });
        if (pI) {
          tl.to(pI, { autoAlpha: 0, scale: 1 }, 0)
            .to(pT, { autoAlpha: 0, y: 14 }, 0);
          pT.style.pointerEvents = 'none';
        }
        tl.to(nI, { autoAlpha: 1 }, 0.1)
          .fromTo(nI, { scale: 1.02 }, { scale: 1.06, duration: 1, ease: 'power2.out' }, 0.1)
          .fromTo(nT, { autoAlpha: 0, y: 20 }, { autoAlpha: 1, y: 0, duration: 0.9, ease: 'power2.out' }, 0.22);
        nT.style.pointerEvents = 'auto';
        current = next;
      };
      var update = function () {
        tick = false; // always release the latch, even on early return
        var total = pin.offsetHeight - window.innerHeight;
        if (total <= 0) return;
        var p = Math.min(1, Math.max(0, -pin.getBoundingClientRect().top / total));
        show(Math.min(imgs.length - 1, Math.floor(p * imgs.length)));
      };
      var onScroll = function () { if (!tick) { tick = true; requestAnimationFrame(update); } };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      update();
      teardown = function () {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
        for (var ki = 0; ki < imgs.length; ki++) gsap.killTweensOf([imgs[ki], texts[ki]]);
        container.classList.remove('steps--pinned');
        container.innerHTML = staticHTML;
        teardown = null;
      };
    }

    var onBreakpoint = function () {
      if (teardown) teardown();
      build();
    };
    if (STEPS_MQ.addEventListener) STEPS_MQ.addEventListener('change', onBreakpoint);
    else STEPS_MQ.addListener(onBreakpoint); // older Safari

    var onSectionUnload = function (event) {
      if (event.target !== container && !event.target.contains(container)) return;
      if (teardown) teardown();
      if (STEPS_MQ.removeEventListener) STEPS_MQ.removeEventListener('change', onBreakpoint);
      else STEPS_MQ.removeListener(onBreakpoint);
      document.removeEventListener('shopify:section:unload', onSectionUnload);
    };
    document.addEventListener('shopify:section:unload', onSectionUnload);
    build();
  }

  /* --- Card gallery slider (mobile): scroll-snap row with prev/next
         buttons and pagination dots. The buttons/dots are display:none
         on desktop where all cards fit, so listeners just sit idle. */
  function initCardSlider(wrap) {
    if (!guard(wrap)) return;
    var scroller = wrap.querySelector('[data-slider-track]') || wrap.querySelector('.cards3'),
        dots = wrap.querySelectorAll('.cards-controls__dot'),
        prev = wrap.querySelector('[data-slide-prev]'),
        next = wrap.querySelector('[data-slide-next]'),
        status = wrap.querySelector('[data-slider-status]');
    if (!scroller || !prev || !next) return;
    var cards = scroller.children;
    function step() { return cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : 0; }
    function setDots() {
      var s = step(), i = s ? Math.round(scroller.scrollLeft / s) : 0;
      i = Math.max(0, Math.min(i, dots.length - 1));
      for (var d = 0; d < dots.length; d++) dots[d].classList.toggle('is-active', d === i);
      if (status) status.textContent = 'Card ' + (i + 1) + ' of ' + cards.length;
    }
    prev.addEventListener('click', function () { scroller.scrollBy({ left: -step(), behavior: REDUCE ? 'auto' : 'smooth' }); });
    next.addEventListener('click', function () { scroller.scrollBy({ left: step(), behavior: REDUCE ? 'auto' : 'smooth' }); });
    var tick = false;
    scroller.addEventListener('scroll', function () {
      if (tick) return; tick = true;
      requestAnimationFrame(function () { tick = false; setDots(); });
    }, { passive: true });
    setDots();
  }

  /* --- Signup forms: every newsletter form (snippets/signup-form.liquid
         and the popup) subscribes the email to one Klaviyo list through
         the public Client Subscribe API. The submit is always intercepted
         so the visitor STAYS ON THIS PAGE (Lauren, 2026-07-24). Klaviyo
         answers 202 once it accepts the subscription, so success shows
         only after that; a network, ad-blocker, or config failure shows
         the form's error line and leaves the form retryable. The company
         ID and list IDs come from theme settings via window.throneKlaviyo
         (theme.liquid); a form whose list is unset shows its error.
         Native validation runs before submit fires, so a firing submit
         means the email is valid. If the hidden "website" bot trap has
         a value we drop the signup but still render success, so bots
         learn nothing. */
  // Fires only after Klaviyo accepts the subscription. Shopify carries this
  // event into the pixel sandbox; DOM form listeners in GTM cannot see the
  // storefront form. Never include contact details here.
  function publishNewsletterSubmission(form) {
    if (window.Shopify && window.Shopify.designMode) return;
    var analytics = window.Shopify && window.Shopify.analytics;
    if (!analytics || typeof analytics.publish !== 'function') return;
    var placement = form.getAttribute('data-signup-placement');
    if (['footer', 'blog', 'article_offer', 'article_rail', 'article_inline', 'intro', 'popup', 'families_waitlist', 'quiz'].indexOf(placement) === -1) return;
    try {
      var result = analytics.publish('throne_newsletter_submitted', {
        form_name: 'newsletter', form_placement: placement
      });
      // Analytics failure must not turn an accepted signup into a retry.
      if (result && typeof result.catch === 'function') result.catch(function () {});
    } catch (e) { /* Signup remains successful if analytics is unavailable. */ }
  }

  // Resolves once Klaviyo accepts the subscription; rejects otherwise.
  // The $50-offer forms (popup, article offer, quiz) join the offer list, whose
  // flow sends the code; every other form joins the newsletter list.
  // signup_source is the form placement (footer, blog, article_rail, article_inline,
  // intro, popup, families_waitlist, quiz); the popup adds its interest answer
  // and the quiz its answers (quiz_*). All land as profile properties for
  // segments and flow filters.
  function subscribeNewsletter(form, email, interest, extra) {
    var cfg = window.throneKlaviyo;
    var placement = form.getAttribute('data-signup-placement') || 'website';
    var offer = placement === 'popup' || placement === 'article_offer' || placement === 'quiz';
    var listId = cfg && (offer ? cfg.offerListId : cfg.newsletterListId);
    if (!cfg || !cfg.companyId || !listId || !window.fetch) {
      return Promise.reject(new Error('Newsletter service unavailable'));
    }
    var properties = { signup_source: placement };
    if (interest) properties.interest = interest;
    if (extra) Object.keys(extra).forEach(function (key) { properties[key] = extra[key]; });
    return window.fetch('https://a.klaviyo.com/client/subscriptions?company_id=' + encodeURIComponent(cfg.companyId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', revision: '2025-07-15' },
      body: JSON.stringify({ data: { type: 'subscription', attributes: {
        custom_source: 'Throne website: ' + placement,
        profile: { data: { type: 'profile', attributes: {
          email: email,
          properties: properties,
          subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
        } } }
      }, relationships: { list: { data: { type: 'list', id: listId } } } } })
    }).then(function (res) {
      if (!res.ok) throw new Error('Newsletter signup rejected (' + res.status + ')');
      publishNewsletterSubmission(form);
    });
  }

  // The quiz (sections/landing-quiz) signs up through the same request, the
  // same lists and the same Shopify signup event as the forms here.
  window.throneSignup = {
    subscribe: function (placement, email, properties) {
      var form = { getAttribute: function (name) { return name === 'data-signup-placement' ? placement : null; } };
      return subscribeNewsletter(form, email, null, properties).then(function () {
        try { localStorage.setItem('throne_subscribed', 'true'); document.documentElement.classList.add('is-subscribed'); } catch (e) {}
      });
    }
  };

  function initSignup(form) {
    if (!guard(form)) return;
    var msg = form.querySelector('[data-signup-msg]');
    var btn = form.querySelector('button[type="submit"]');
    var submitted = false;
    function busy(on) {
      form.classList.toggle('is-submitting', on);
      if (!btn) return;
      btn.disabled = on;
      if (on) btn.setAttribute('aria-busy', 'true'); else btn.removeAttribute('aria-busy');
    }
    function succeed() {
      busy(false);
      // success message replaces the field, mirroring the old server render
      form.querySelectorAll('label, input, button').forEach(function (el) { el.hidden = true; });
      msg.classList.remove('signup__msg--error');
      msg.textContent = form.getAttribute('data-success-text') || 'Thanks — you’re on the list.';
      msg.hidden = false;
    }
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (!msg || submitted || form.classList.contains('is-submitting')) return;
      if (!form.reportValidity()) return;
      var emailField = form.querySelector('input[type="email"]');
      var trap = form.querySelector('input[name="website"]');
      var email = emailField ? emailField.value.trim() : '';
      if (!email) return;
      submitted = true;
      // design-mode and bot-trap submits stay local
      if ((window.Shopify && window.Shopify.designMode) || (trap && trap.value)) { succeed(); return; }
      busy(true);
      msg.hidden = true;
      subscribeNewsletter(form, email).then(succeed, function () {
        submitted = false;
        busy(false);
        msg.classList.add('signup__msg--error');
        msg.textContent = form.getAttribute('data-error-text') || 'Something went wrong — please try again.';
        msg.hidden = false;
      });
    });
  }
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    document.querySelectorAll('form.signup.is-submitting').forEach(function (form) {
      form.classList.remove('is-submitting');
      var btn = form.querySelector('button[type="submit"]');
      if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); }
    });
  });

  /* --- Signup popup (Webflow modal rebuild, 2026-07-28): interest
         question first, then email capture. Auto-opens ONCE per pageview
         at the section's scroll-depth setting; a completed signup writes
         localStorage.throne_subscribed (same key as the old Webflow
         embed) which suppresses it for good. Submit uses the same Klaviyo
         subscribeNewsletter as initSignup, adding the interest answer as a
         profile property; the confirmation step shows only once Klaviyo
         accepts. A failure shows an error without suppressing future
         signup attempts. In the theme editor it never
         auto-opens; selecting the section opens it, deselecting closes. */
  function initSignupPopup(overlay) {
    if (!guard(overlay)) return;
    var panels = {
      interest: overlay.querySelector('[data-spop-step="interest"]'),
      email: overlay.querySelector('[data-spop-step="email"]'),
      confirm: overlay.querySelector('[data-spop-step="confirm"]')
    };
    if (!panels.interest || !panels.email || !panels.confirm) return;
    var form = overlay.querySelector('form');
    var emailInput = overlay.querySelector('[data-spop-email]');
    var interestInput = overlay.querySelector('[data-spop-interest]');
    var err = overlay.querySelector('.spop__err');
    var designMode = window.Shopify && window.Shopify.designMode;
    var submitted = false;
    var lastPopFocus = null;

    function subscribed() {
      try { return !!localStorage.getItem('throne_subscribed'); } catch (e) { return false; }
    }
    function showStep(name) {
      Object.keys(panels).forEach(function (k) {
        panels[k].classList.toggle('is-active', k === name);
      });
    }
    function open() {
      if (overlay.classList.contains('open')) return;
      overlay.classList.add('open');
      overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      lastPopFocus = document.activeElement;
      // focus the card container (tabindex=-1, dialog pattern) — focusing the
      // close button painted a :focus-visible ring on scroll-triggered opens
      var card = overlay.querySelector('.spop__card');
      if (card) card.focus();
    }
    function close() {
      if (!overlay.classList.contains('open')) return;
      overlay.classList.remove('open');
      overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      setTimeout(reset, 320); // after the close, off-screen (Webflow's beat)
      if (lastPopFocus && lastPopFocus.focus) lastPopFocus.focus();
    }
    function reset() {
      if (emailInput) { emailInput.value = ''; emailInput.removeAttribute('aria-invalid'); }
      if (err) err.hidden = true;
      overlay.querySelectorAll('[data-spop-option]').forEach(function (o) { o.setAttribute('aria-checked', 'false'); });
      if (designMode) submitted = false; // customizer can re-walk the flow
      showStep('interest');
    }

    // auto-open at scroll depth, once (never in the editor)
    var depth = parseInt(overlay.getAttribute('data-scroll-depth'), 10);
    depth = (isNaN(depth) ? 30 : depth) / 100;
    if (!designMode && !subscribed()) {
      var fired = false;
      var onScroll = function () {
        if (fired) return;
        var max = document.documentElement.scrollHeight - window.innerHeight;
        if (max <= 0) return;
        if (window.scrollY / max >= depth) {
          fired = true;
          window.removeEventListener('scroll', onScroll);
          open();
        }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
    }

    // interest options: mark the pick, then advance on Webflow's 220ms beat
    overlay.querySelectorAll('[data-spop-option]').forEach(function (opt) {
      opt.addEventListener('click', function () {
        overlay.querySelectorAll('[data-spop-option]').forEach(function (o) {
          o.setAttribute('aria-checked', String(o === opt));
        });
        if (interestInput) interestInput.value = opt.getAttribute('data-value') || '';
        setTimeout(function () {
          showStep('email');
          if (emailInput) emailInput.focus();
        }, 220);
      });
    });
    var skip = overlay.querySelector('[data-spop-skip]');
    if (skip) skip.addEventListener('click', function () {
      if (interestInput) interestInput.value = skip.getAttribute('data-value') || '';
      showStep('email');
      if (emailInput) emailInput.focus();
    });
    var back = overlay.querySelector('[data-spop-back]');
    if (back) back.addEventListener('click', function () { showStep('interest'); });

    overlay.querySelectorAll('[data-spop-close]').forEach(function (btn) {
      btn.addEventListener('click', close);
    });
    overlay.addEventListener('click', function (e) { if (e.target === overlay) close(); });
    document.addEventListener('keydown', function (e) {
      if (!overlay.classList.contains('open')) return;
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab') return;
      // trap Tab inside the dialog (house mobile-menu pattern)
      var vis = Array.prototype.filter.call(
        overlay.querySelectorAll('button, input, a[href]'),
        function (el) { return el.offsetParent !== null; }
      );
      if (!vis.length) return;
      var first = vis[0], last = vis[vis.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    // email validation mirrors the Webflow regex; typing clears the error
    var validationErrorText = err ? err.textContent : '';
    function validEmail(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim()); }
    if (emailInput) emailInput.addEventListener('input', function () {
      emailInput.removeAttribute('aria-invalid');
      if (err) { err.hidden = true; err.textContent = validationErrorText; }
    });
    // signup lands on the confirmation step (1254:1599) instead of closing;
    // design-mode submits stay local — no Klaviyo profile, no suppression flag —
    // so the customizer can walk the flow with a fake email safely
    if (form) form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (submitted) return;
      var email = emailInput ? emailInput.value.trim() : '';
      if (!validEmail(email) || !form.reportValidity()) {
        if (err) { err.textContent = validationErrorText; err.hidden = false; }
        if (emailInput) { emailInput.setAttribute('aria-invalid', 'true'); emailInput.focus(); }
        return;
      }
      submitted = true;
      var interest = interestInput ? interestInput.value : '';
      var trap = form.querySelector('input[name="website"]');
      var submitBtn = form.querySelector('button[type="submit"]');
      function done() {
        if (submitBtn) { submitBtn.disabled = false; submitBtn.removeAttribute('aria-busy'); }
        if (!designMode) { try { localStorage.setItem('throne_subscribed', 'true'); } catch (e2) {} }
        showStep('confirm');
        var codeBtn = overlay.querySelector('[data-spop-copy]');
        if (codeBtn) codeBtn.focus();
      }
      if (designMode || (trap && trap.value)) { done(); return; }
      if (submitBtn) { submitBtn.disabled = true; submitBtn.setAttribute('aria-busy', 'true'); }
      if (err) err.hidden = true;
      subscribeNewsletter(form, email, interest).then(done, function () {
        submitted = false;
        if (submitBtn) { submitBtn.disabled = false; submitBtn.removeAttribute('aria-busy'); }
        if (err) { err.textContent = form.getAttribute('data-error-text') || 'Something went wrong — please try again.'; err.hidden = false; }
      });
    });

    // discount code box: click copies the code, toast confirms.
    // toast is a live region filled on copy (and emptied after), so screen
    // readers announce it exactly once per copy.
    var copyBtn = overlay.querySelector('[data-spop-copy]');
    var toast = overlay.querySelector('[data-spop-toast]');
    var toastTimer;
    function showToast() {
      if (!toast) return;
      toast.textContent = toast.getAttribute('data-copied-text') || 'Copied to clipboard';
      toast.classList.add('is-visible');
      clearTimeout(toastTimer);
      toastTimer = setTimeout(function () {
        toast.classList.remove('is-visible');
        toast.textContent = '';
      }, 2000);
    }
    function copyFallback(text) {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      var ok = false;
      try { ok = document.execCommand('copy'); } catch (e) {}
      document.body.removeChild(ta);
      return ok;
    }
    if (copyBtn) copyBtn.addEventListener('click', function () {
      var code = copyBtn.getAttribute('data-code') || '';
      if (!code) return;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(showToast, function () {
          if (copyFallback(code)) showToast();
        });
      } else if (copyFallback(code)) {
        showToast();
      }
    });

    // theme editor: open while the section is selected so it can be styled
    if (designMode) {
      document.addEventListener('shopify:section:select', function (e) { if (e.target.contains(overlay)) open(); });
      document.addEventListener('shopify:section:deselect', function (e) { if (e.target.contains(overlay)) close(); });
    }
  }

  /* --- How It Works insight timeline: server-rendered stages remain
         readable as stacked cards without JavaScript. Enhancement turns
         them into one keyboard-operable timeline panel. */
  function initHiwInsights(root) {
    if (!guard(root)) return;
    var list = root.querySelector('.hiw-insights__stage-list');
    if (!list) return;
    var stages = Array.prototype.slice.call(list.querySelectorAll('[data-hiw-insight-stage]'));
    if (!stages.length) return;

    function selectStage(index, moveFocus) {
      if (index < 0 || index >= stages.length) return;
      stages.forEach(function (stage, stageIndex) {
        var selected = stageIndex === index;
        var tab = stage.querySelector('[data-hiw-insight-tab]');
        var panel = stage.querySelector('[data-hiw-insight-panel]');
        stage.classList.toggle('is-active', selected);
        if (tab) {
          tab.setAttribute('aria-selected', String(selected));
          tab.tabIndex = selected ? 0 : -1;
        }
        if (panel) panel.hidden = !selected;
      });
      if (moveFocus) {
        var activeTab = stages[index].querySelector('[data-hiw-insight-tab]');
        if (activeTab) activeTab.focus();
      }
    }

    list.classList.add('is-enhanced');
    selectStage(0, false);

    list.addEventListener('click', function (event) {
      var tab = event.target.closest('[data-hiw-insight-tab]');
      if (!tab || !list.contains(tab)) return;
      var stage = tab.closest('[data-hiw-insight-stage]');
      selectStage(stages.indexOf(stage), false);
    });

    list.addEventListener('keydown', function (event) {
      var tab = event.target.closest('[data-hiw-insight-tab]');
      if (!tab || !list.contains(tab)) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight' && event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
      event.preventDefault();
      var current = stages.indexOf(tab.closest('[data-hiw-insight-stage]'));
      var direction = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1;
      selectStage((current + direction + stages.length) % stages.length, true);
    });
  }

  function initHiwFaq(root) {
    if (!guard(root)) return;
    var list = root.querySelector('[data-hiw-faq-list]');
    var toggle = root.querySelector('[data-hiw-faq-toggle]');
    var label = root.querySelector('[data-hiw-faq-toggle-label]');
    if (!list || !toggle || !label) return;
    var items = Array.prototype.slice.call(list.querySelectorAll('[data-hiw-faq-item]'));
    var visibleCount = parseInt(root.getAttribute('data-hiw-faq-visible'), 10) || 5;
    if (items.length <= visibleCount) return;
    var showAll = root.getAttribute('data-hiw-faq-show-all') || label.textContent;
    var showLess = root.getAttribute('data-hiw-faq-show-less') || showAll;

    function setExpanded(expanded) {
      items.forEach(function (item, index) {
        item.hidden = !expanded && index >= visibleCount;
      });
      toggle.setAttribute('aria-expanded', String(expanded));
      label.textContent = expanded ? showLess : showAll;
    }

    toggle.hidden = false;
    setExpanded(false);
    toggle.addEventListener('click', function () {
      setExpanded(toggle.getAttribute('aria-expanded') !== 'true');
    });
  }

  document.addEventListener('shopify:block:select', function (event) {
    var insightStage = event.target.closest && event.target.closest('[data-hiw-insight-stage]');
    if (insightStage) {
      var insightTab = insightStage.querySelector('[data-hiw-insight-tab]');
      if (insightTab) insightTab.click();
    }
    var faqItem = event.target.closest && event.target.closest('[data-hiw-faq-item]');
    if (faqItem) {
      var faqRoot = faqItem.closest('[data-hiw-faq]');
      var faqToggle = faqRoot && faqRoot.querySelector('[data-hiw-faq-toggle]');
      if (faqItem.hidden && faqToggle && faqToggle.getAttribute('aria-expanded') !== 'true') faqToggle.click();
      faqItem.open = true;
    }
  });

  /* --- Past the first section: html.is-past-hero once the page's first section (the hero) has
         scrolled fully out of view, removed again on the way back up. Floating extras wait for it
         (the Alia signup tab, see styles.css). No observer support: set it at once. */
  function initPastHero() {
    var html = document.documentElement;
    var first = document.querySelector('#MainContent > .shopify-section');
    if (!first || !('IntersectionObserver' in window)) { html.classList.add('is-past-hero'); return; }
    new IntersectionObserver(function (entries) {
      html.classList.toggle('is-past-hero', !entries[0].isIntersecting);
    }).observe(first);
  }

  /* In-page links like the menu's "#faq" only work on pages with that section; elsewhere they go to
     the homepage's. Links that open a modal (data-modal) are left alone. Registered once, on the document, so it covers menus rendered at any time. */
  function initHashFallback() {
    document.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('a[href^="#"]');
      if (!a || a.hasAttribute('data-modal') || a.hash.length < 2 || document.getElementById(decodeURIComponent(a.hash.slice(1)))) return;
      e.preventDefault();
      location.href = (window.Shopify && Shopify.routes && Shopify.routes.root || '/') + a.hash;
    });
  }

  /* --- Init everything inside a root (document, or a section injected
         by the theme editor). Each init is isolated so one broken
         section can't abort the rest of the page's behavior. */
  function initSafely(fn) {
    return function (el) {
      try { fn(el); } catch (e) { console.error('[throne] section init failed:', e); }
    };
  }
  function initAll(root) {
    root = root || document;
    root.querySelectorAll('[data-site-header]').forEach(initSafely(initHeader));
    root.querySelectorAll('[data-steps-pin]').forEach(initSafely(initSteps));
    root.querySelectorAll('[data-card-slider]').forEach(initSafely(initCardSlider));
    root.querySelectorAll('[data-hiw-insights]').forEach(initSafely(initHiwInsights));
    root.querySelectorAll('[data-hiw-faq]').forEach(initSafely(initHiwFaq));
    root.querySelectorAll('[data-signup-popup]').forEach(initSafely(initSignupPopup));
    root.querySelectorAll('form.signup').forEach(initSafely(initSignup));
    // the root itself may be the hooked element (editor injects the section node)
    if (root !== document && root.matches) {
      var rootInits = {
        'data-site-header': initHeader,
        'data-steps-pin': initSteps,
        'data-card-slider': initCardSlider,
        'data-hiw-insights': initHiwInsights,
        'data-hiw-faq': initHiwFaq,
        'data-signup-popup': initSignupPopup
      };
      Object.keys(rootInits).forEach(function (attr) {
        if (root.hasAttribute && root.hasAttribute(attr)) initSafely(rootInits[attr])(root);
      });
    }
  }

  /* Deferred scripts execute at readyState 'interactive', in document
     order — this file runs BEFORE the gsap tags below it in theme.liquid.
     Boot on DOMContentLoaded (fires after the whole deferred batch) so
     initSteps sees window.gsap; 'complete' covers late injection. */
  if (document.readyState === 'complete') {
    initAll(document); initSafely(initPastHero)(); initSafely(initHashFallback)();
  } else {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); initSafely(initPastHero)(); initSafely(initHashFallback)(); });
  }

  // Theme editor: re-init sections as they're (re)rendered.
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
